import {
  PANIC_ALERT_SET_LIST,
  PANIC_ALERT_SET_TOTAL,
  PANIC_ALERT_SET_LOADING_STATUS_LIST,
  PANIC_ALERT_SET_LOADING_STATUS_TOTAL,
  PANIC_ALERT_CLEAR_ALL,
  PANIC_ALERT_SET_SEARCH_PARAMS,
  PANIC_ALERT_UPDATE_READ_STATUS,
  PANIC_ALERT_SET_LOADING_STATUS_SUBMIT
} from '../types'
import panicAlertService from '../../api/panicAlert'
import notify from '@/api/notification'
import analytics from '../../api/analytics'
import strings from '@/strings'
import Moment from 'moment'
import { PANIC_ALERT_PERIOD_DAYS_DEFAULT } from '../../enums'

const state = {
  panicAlerts: [],
  totalPanicAlerts: 0,
  loadingStatusList: 'STATUS_INITIAL',
  loadingStatusSubmit: 'STATUS_INITIAL',
  loadingStatusTotal: 'STATUS_INITIAL',
  panicParams: {
    startDate: Moment().subtract(PANIC_ALERT_PERIOD_DAYS_DEFAULT, 'days').toDate(),
    endDate: Moment().toDate(),
    limit: strings.listsLimits.panicAlerts
  },
  currentTab: 0
}

const mutations = {
  [PANIC_ALERT_SET_LIST](state, panicAlerts) {
    state.panicAlerts = panicAlerts
  },
  [PANIC_ALERT_SET_TOTAL](state, total) {
    state.totalPanicAlerts = total
  },
  [PANIC_ALERT_SET_LOADING_STATUS_LIST](state, status) {
    state.loadingStatusList = status
  },
  [PANIC_ALERT_SET_LOADING_STATUS_SUBMIT](state, status) {
    state.loadingStatusSubmit = status
  },
  [PANIC_ALERT_SET_LOADING_STATUS_TOTAL](state, status) {
    state.loadingStatusTotal = status
  },
  [PANIC_ALERT_SET_SEARCH_PARAMS](state, params) {
    state.panicParams = {
      ...state.panicParams,
      ...params
    }
  },
  [PANIC_ALERT_CLEAR_ALL](state) {
    state.panicAlerts = []
    state.totalPanicAlerts = 0
    state.loadingStatusList = 'STATUS_INITIAL'
    state.loadingStatusSubmit = 'STATUS_INITIAL'
    state.loadingStatusTotal = 'STATUS_INITIAL'
    state.panicParams = {
      startDate: Moment().subtract(PANIC_ALERT_PERIOD_DAYS_DEFAULT, 'days').toDate(),
      endDate: Moment().toDate(),
      limit: strings.listsLimits.panicAlerts
    }
  },
  [PANIC_ALERT_UPDATE_READ_STATUS](state, { index, dateRead }) {
    if (state.panicAlerts[index]) {
      state.panicAlerts[index].dateRead = dateRead
    }
  },
  setCurrentTab(state, tab) {
    state.currentTab = tab
  }
}

const getters = {
  panicAlertList: state => state.panicAlerts,
  panicAlertsTotal: state => state.totalPanicAlerts,
  panicAlertListStatusLoading: state => state.loadingStatusList,
  panicAlertSubmitStatusLoading: state => state.loadingStatusSubmit,
  isPanicAlertListStatusLoading: state => state.loadingStatusList === 'STATUS_LOADING',
  isPanicAlertSubmitStatusLoading: state => state.loadingStatusSubmit === 'STATUS_LOADING',
  isPanicAlertTotalStatusLoading: state => state.loadingStatusTotal === 'STATUS_LOADING',
  panicAlertStartDate: state => state.panicParams.startDate,
  panicAlertEndDate: state => state.panicParams.endDate,
  panicAlertLimit: state => state.panicParams.limit,
  panicAlertParams: state => {
    return {
      startDate: state.panicParams?.startDate,
      endDate: state.panicParams?.endDate,
      limit: state.panicParams.limit
    }
  },
  panicAlertCurrentTab: state => state.currentTab
}

const actions = {
  async fetchPanicAlertList({ commit, getters }, { isSubmit = false }) {
    try {
      const loadingMutation = isSubmit ? PANIC_ALERT_SET_LOADING_STATUS_SUBMIT : PANIC_ALERT_SET_LOADING_STATUS_LIST

      commit(loadingMutation, 'STATUS_LOADING')

      const { startDate, endDate, limit } = getters.panicAlertParams
      const startDateFormatted = startDate ? Moment(startDate).format('DD/MM/YYYY') : undefined
      const endDateFormatted = endDate ? Moment(endDate).format('DD/MM/YYYY') : undefined

      const response = await panicAlertService.getPanicAlerts(limit, startDateFormatted, endDateFormatted)
      const panicAlerts = response.data

      commit(PANIC_ALERT_SET_LIST, panicAlerts)
      commit(loadingMutation, panicAlerts.length === strings.listsLimits.panicAlert ? 'STATUS_SUCCESS' : 'STATUS_END')

      const actionType = isSubmit ? 'submit' : 'infinite loading'
      submitAnalyticsEvent(`Busca por alertas de pânico - ${actionType}`)
    } catch (error) {
      const loadingMutation = isSubmit ? PANIC_ALERT_SET_LOADING_STATUS_SUBMIT : PANIC_ALERT_SET_LOADING_STATUS_LIST

      commit(loadingMutation, 'STATUS_FAILED')
      commit(PANIC_ALERT_SET_LIST, [])

      notify({
        title: 'AVISO:',
        message: 'Erro ao carregar informações. Por favor, tente novamente.',
        action: { title: 'RECARREGAR', handler: reload }
      })

      const actionType = isSubmit ? 'submit' : 'infinite loading'
      submitAnalyticsEvent(`Erro ao buscar alertas de pânico - ${actionType}`)
    }
  },

  async getPanicAlertList({ dispatch }) {
    await dispatch('fetchPanicAlertList', { isSubmit: false })
  },

  async submitPanicAlertSearch({ dispatch }) {
    await dispatch('fetchPanicAlertList', { isSubmit: true })
  },

  async getPanicAlertsTotalNumber({ commit, getters }) {
    try {
      commit(PANIC_ALERT_SET_LOADING_STATUS_TOTAL, 'STATUS_LOADING')

      const { startDate, endDate, limit } = getters.panicAlertParams
      const startDateFormatted = startDate ? Moment(startDate).format('DD/MM/YYYY') : undefined
      const endDateFormatted = endDate ? Moment(endDate).format('DD/MM/YYYY') : undefined

      const response = await panicAlertService.getTotalPanicAlerts(limit, startDateFormatted, endDateFormatted)
      commit(PANIC_ALERT_SET_TOTAL, response.data?.batchesCount ?? 0)
      commit(PANIC_ALERT_SET_LOADING_STATUS_TOTAL, 'STATUS_SUCCESS')

      submitAnalyticsEvent('Total de alertas de pânico - sucesso')
    } catch (error) {
      commit(PANIC_ALERT_SET_LOADING_STATUS_TOTAL, 'STATUS_END')
      commit(PANIC_ALERT_SET_TOTAL, 0)

      submitAnalyticsEvent('Falha ao buscar total de alertas de pânico')

      if (error.response?.status !== 404) {
        commit(PANIC_ALERT_SET_LOADING_STATUS_TOTAL, 'STATUS_FAILED')
        notify({
          title: 'AVISO:',
          message: 'Erro ao carregar informações. Por favor, tente novamente.',
          action: { title: 'RECARREGAR', handler: reload }
        })
      }
    }
  },

  async readPanicAlert({ commit }, status) {
    const { id, index } = status

    try {
      const response = await panicAlertService.readPanicAlert(id)
      if (response.status === 200) {
        const dateRead = Moment().format('YYYY-MM-DD HH:mm:ss')
        commit(PANIC_ALERT_UPDATE_READ_STATUS, { index, dateRead })
        submitAnalyticsEvent(`Alerta de pânico lido - ID: ${id}`)
      }
    } catch (error) {
      notify({
        title: 'AVISO:',
        message: 'Erro ao registrar leitura do alerta. Por favor, tente novamente.',
        action: { title: 'RECARREGAR', handler: reload }
      })
      submitAnalyticsEvent(`Erro ao ler alerta de pânico - ID: ${id}`)
    }
  },

  clearAllPanicAlertData({ commit }) {
    commit(PANIC_ALERT_CLEAR_ALL)
  },

  selectPanicParams({ commit }, params) {
    commit(PANIC_ALERT_SET_SEARCH_PARAMS, params)
  },

  setWaitingPanicAlertLoadingStatus({ commit }) {
    commit(PANIC_ALERT_SET_LOADING_STATUS_LIST, 'STATUS_WAITING')
  },

  updatePanicAlertDates({ commit }, { startDate, endDate }) {
    commit(PANIC_ALERT_SET_SEARCH_PARAMS, { startDate, endDate })
  },

  changeTab({ commit }, tab) {
    commit('setCurrentTab', tab)
  }
}

const reload = () => window.location.reload()

const submitAnalyticsEvent = action => {
  analytics.pushAnalyticsEvent('ga-event', {
    category: 'Alerta de Pânico',
    action
  })
}

export default {
  state,
  mutations,
  actions,
  getters
}
